import { AzureMapClient, EntityType } from 'clients/AzureMapClient';

export type AddressSearchType = 'default' | 'structured' | 'fuzzy';

type PostalCode = string;

export type Municipality = {
  municipality: string;
  postalCode: string;
};

type StreetNumber = string;

type Street = {
  address: string;
  municipality: string;
  municipalitySubdivision: string;
};

type PointAddress = {
  address: string;
  municipality: string;
  municipalitySubdivision: string;
  streetNumber: StreetNumber;
};

export interface AddressSearchObject {
  postalCode?: string;
  municipality?: string;
  streetName?: string;
  streetNumber?: string;
}

export interface SearchResult {
  municipalities: Municipality[];
  streets: Street[];
  pointAddresses: PointAddress[];
  postalCodes: PostalCode[];
}

export class AddressSearchService {
  private filterMunicipalitiesAndSubdivisions(results: any, postalCode?: string): Municipality[] {
    function getUniqueListBy(arr: [], key: any) {
      return Array.from(new Map(arr.map((item) => [item[key], item])).values());
    }

    const res = results
      .filter((searchResult: any) => {
        const hasStreetName = searchResult.address.streetName !== ''; // ignore results with empty streetname
        const isCorrectType = [
          EntityType.Geography,
          EntityType.Street,
          EntityType.PointAddress
        ].includes(searchResult.type);

        return hasStreetName && isCorrectType;
      })
      .map((municipalityObject: any) => {
        let splittedMunicipalities = municipalityObject.address.municipality?.split(', ') || [];

        splittedMunicipalities = splittedMunicipalities.map((municipality: string) => {
          return {
            municipality: municipality,
            postalCode: municipalityObject.address.postalCode
          };
        });

        let municipalitySubdivisions =
          municipalityObject.address.municipalitySubdivision?.split(', ') || [];

        municipalitySubdivisions = municipalitySubdivisions.map((subdiv: string) => {
          return {
            municipality: subdiv,
            postalCode: municipalityObject.address.postalCode
          };
        });

        return [...splittedMunicipalities, ...municipalitySubdivisions];
      })
      .flat();

    return getUniqueListBy(res, 'municipality');
  }

  private filterStreets(results: any) {
    return results
      .filter((searchResult: any) => {
        return (
          searchResult.address.streetName !== '' &&
          [EntityType.Street, EntityType.PointAddress].includes(searchResult.type)
        );
      })
      .map((streetObject: any) => {
        return {
          address: streetObject.address.streetName,
          municipality: streetObject.address.municipality,
          municipalitySubdivision: streetObject.address.municipalitySubdivision
        };
      });
  }

  private filterPointAddress(results: any): PointAddress[] {
    return results
      .filter((searchResult: any) => searchResult.type === EntityType.PointAddress)
      .map((pointAddressObject: any) => {
        return {
          address: pointAddressObject.address.streetName,
          municipality: pointAddressObject.address.municipality,
          municipalitySubdivision: pointAddressObject.address.municipalitySubdivision,
          streetNumber: pointAddressObject.address.streetNumber
        };
      });
  }

  private async defaultSearch(address: AddressSearchObject): Promise<SearchResult> {
    const {
      data: { results }
    } = await AzureMapClient.searchAddress(address);

    return {
      municipalities: this.filterMunicipalitiesAndSubdivisions(results, address.postalCode),
      streets: this.filterStreets(results),
      pointAddresses: this.filterPointAddress(results),
      postalCodes: []
    };
  }

  private async structuredSearch(address: AddressSearchObject): Promise<SearchResult> {
    const {
      data: { results: result }
    } = await AzureMapClient.searchAddress(address, true);

    return result;
  }

  private fuzzySearch() {
    throw new Error('Not implemented yet.');
  }

  public async search(address: AddressSearchObject, searchType: AddressSearchType = 'default') {
    switch (searchType) {
      case 'fuzzy':
        this.fuzzySearch();
        break;
      case 'structured':
        return this.structuredSearch(address);
      case 'default':
      default:
        return this.defaultSearch(address);
    }
  }
}
